import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type RecordHit, useDatabaseRecordSearch } from '@/entities/database';
import { useDebounced } from '@/shared/lib/use-debounced';
import { Button, Dialog, EmptyState, ErrorState, LoadingState, TextField } from '@/shared/ui';

import styles from './DatabaseSearchDialog.module.css';

interface Props {
  databaseId: string;
  /** Open the record `recordId` of table `tableId`. */
  onOpen: (hit: RecordHit) => void;
  onClose: () => void;
}

/** The matched part of `text` is wrapped in <mark>; the match is case-insensitive and literal. */
function Highlighted({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark>{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/**
 * "Search in database": records of EVERY table (not table names), found by the server. Each hit
 * names its table and the matching cells; choosing one opens that table with that record.
 * Per-table search and filters stay where they were.
 */
export function DatabaseSearchDialog({ databaseId, onOpen, onClose }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const query = useDebounced(text.trim(), 300);
  const search = useDatabaseRecordSearch(databaseId, query);
  const pages = search.data?.pages ?? [];
  const hits = pages.flatMap((page) => page.items);
  const first = pages[0];
  const waiting = text.trim() !== query || search.isFetching;

  return (
    <Dialog title={t('databases.recordSearch.title')} onClose={onClose} size="lg">
      <div className={styles.body}>
        <TextField
          label={t('databases.recordSearch.label')}
          type="search"
          value={text}
          autoFocus
          maxLength={200}
          placeholder={t('databases.recordSearch.placeholder')}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        {query === '' ? (
          <p className={styles.hint}>{t('databases.recordSearch.hint')}</p>
        ) : search.isError ? (
          <ErrorState
            error={search.error}
            onRetry={() => {
              void search.refetch();
            }}
          />
        ) : search.isPending ? (
          <LoadingState />
        ) : hits.length === 0 ? (
          <EmptyState title={t('databases.recordSearch.none', { query })} />
        ) : (
          <>
            <p className={styles.summary} role="status" aria-live="polite">
              {t('databases.recordSearch.summary', { count: first?.total ?? hits.length })}
            </p>
            <ul className={styles.list} aria-busy={waiting}>
              {hits.map((hit) => (
                <li key={hit.record_id}>
                  <button
                    type="button"
                    className={styles.hit}
                    onClick={() => {
                      onOpen(hit);
                    }}
                  >
                    <span className={styles.table}>{hit.table_name}</span>
                    <span className={styles.title}>
                      {hit.title ? (
                        <Highlighted text={hit.title} query={query} />
                      ) : (
                        t('databases.recordSearch.untitled')
                      )}
                    </span>
                    <span className={styles.cells}>
                      {hit.cells.map((cell) => (
                        <Fragment key={cell.column_id}>
                          <span className={styles.column}>{cell.column_name}</span>
                          <span className={styles.value}>
                            <Highlighted text={cell.text} query={query} />
                          </span>
                        </Fragment>
                      ))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {search.hasNextPage && (
              <Button
                disabled={search.isFetchingNextPage}
                onClick={() => {
                  void search.fetchNextPage();
                }}
              >
                {t('databases.recordSearch.more')}
              </Button>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
